<div class="container-margin-page">
  <form [formGroup]="containerMarginForm">

    <!-- Header + Basic Info -->
    <mat-card>
      <mat-card-content>
        @if (isEditMode && containerMarginForm.get('refNo')?.value) {
          <mat-form-field appearance="fill" class="short-field">
            <mat-label>Ref No</mat-label>
            <input matInput formControlName="refNo" readonly autocomplete="off">
          </mat-form-field>
        }

        <mat-form-field appearance="fill" class="segment-field">
          <mat-label>Select Business Segment</mat-label>
          <mat-select formControlName="businessSegmentId" required>
            @for (s of filteredSegments | async; track s) {
              <mat-option [value]="s.id">
                {{ s.businessSegmentName }} ({{ s.businessSegmentCode }})
              </mat-option>
            }
          </mat-select>
          @if (containerMarginForm.get('businessSegmentId')?.hasError('required')) {
            <mat-error>Business Segment is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="fill" class="short-field">
          <mat-label>WEF Date</mat-label>
          <input matInput [matDatepicker]="wefDatePicker" formControlName="wefDate" [min]="today"
            required autocomplete="off">
          <mat-datepicker-toggle matSuffix [for]="wefDatePicker"></mat-datepicker-toggle>
          <mat-datepicker #wefDatePicker></mat-datepicker>
          @if (containerMarginForm.get('wefDate')?.hasError('required')) {
            <mat-error>WEF Date is required</mat-error>
          }
        </mat-form-field>
      </mat-card-content>
    </mat-card>

    <!-- Standard Profit Margin -->
    <mat-card class="section-card">
      <mat-card-content>
        <h4 class="section-title"><span class="title-bar"></span>Standard Profit Margin</h4>

        <div class="standard-table-wrap">
          <table mat-table [dataSource]="standardMargins.controls" formArrayName="standardMargins"
            class="margin-table">

            <ng-container matColumnDef="container">
              <th mat-header-cell *matHeaderCellDef>Container</th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <span class="container-label">{{ row.get('containerType')?.value }}</span>
              </td>
            </ng-container>

            <ng-container matColumnDef="marginPercentage">
              <th mat-header-cell *matHeaderCellDef>Margin % age on All-in Freight Cost</th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <mat-form-field appearance="fill" class="small-field">
                  <input matInput formControlName="marginPercentage" required autocomplete="off"
                    (keypress)="restrictDecimalInput($event, 3)">
                  <span matSuffix class="percentage-symbol">%</span>
                  @if (row.get('marginPercentage')?.hasError('pattern')) {
                    <mat-error>Max 5 digits &amp; 3 decimals</mat-error>
                  }
                </mat-form-field>
              </td>
            </ng-container>

            <ng-container matColumnDef="minimumMargin">
              <th mat-header-cell *matHeaderCellDef>Minimum Margin</th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <mat-form-field appearance="fill" class="small-field">
                  <input matInput formControlName="minimumMargin" required autocomplete="off"
                    (keypress)="restrictDecimalInput($event, 3)">
                  @if (row.get('minimumMargin')?.hasError('pattern')) {
                    <mat-error>Max 5 digits &amp; 3 decimals</mat-error>
                  }
                </mat-form-field>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="standardMarginColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: standardMarginColumns"></tr>
          </table>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Profit Margin — Exceptions -->
    <mat-card class="section-card">
      <mat-card-content>
        <div class="section-title-row">
          <h4 class="section-title"><span class="title-bar"></span> Profit Margin — Exceptions </h4>
          <button mat-stroked-button color="primary" type="button" class="add-exception-btn"
            (click)="addException()">
            + Add Exception
          </button>
        </div>

        <div class="exception-table-scroll">
          <table mat-table [dataSource]="exceptionDataSource" formArrayName="exceptionMargins"
            class="margin-table">

            <ng-container matColumnDef="port">
              <th mat-header-cell *matHeaderCellDef>
                Port
                <div class="exception-filter-wrap">
                  <input type="search" class="exception-filter-input" placeholder="Search port..."
                    autocomplete="off" [formControl]="exceptionPortFilter">
                </div>
              </th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <mat-form-field appearance="fill">
                  <mat-select placeholder="Select Port" formControlName="portId"
                    (selectionChange)="setPortValueChangeEvent($event.value, i)">
                    <mat-option>
                      <ngx-mat-select-search [searching]="searching" [formControl]="portSearchCtrls[i]"
                        noEntriesFoundLabel="No matching data found" placeholderLabel="Search Port">
                        <mat-icon ngxMatSelectSearchClear>delete</mat-icon>
                      </ngx-mat-select-search>
                    </mat-option>
                    @for (port of filteredPorts[i] | async; track port) {
                      <mat-option [value]="port.id">
                        {{ port.name }} - ({{ port.code }})
                      </mat-option>
                    }
                  </mat-select>
                  @if (row.get('portId')?.hasError('required')) {
                    <mat-error>Port is required</mat-error>
                  }
                </mat-form-field>
              </td>
            </ng-container>

            <ng-container matColumnDef="country">
              <th mat-header-cell *matHeaderCellDef>
                Country
                <div class="exception-filter-wrap">
                  <input type="search" class="exception-filter-input" placeholder="Search country..."
                    autocomplete="off" [formControl]="exceptionCountryFilter">
                </div>
              </th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <mat-form-field appearance="fill">
                  <input matInput placeholder="Country Name" formControlName="countryName" readonly
                    autocomplete="off">
                </mat-form-field>
              </td>
            </ng-container>

            <ng-container matColumnDef="margin20ft">
              <th mat-header-cell *matHeaderCellDef>20ft Margin % age <br>on All-in Freight Cost</th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <mat-form-field appearance="fill" class="small-field">
                  <input matInput formControlName="margin20ft" autocomplete="off"
                    (keypress)="restrictDecimalInput($event, 3)">
                  <span matSuffix class="percentage-symbol">%</span>
                  @if (row.get('margin20ft')?.hasError('required')) {
                    <mat-error>20ft Margin is required</mat-error>
                  }
                  @if (row.get('margin20ft')?.hasError('pattern')) {
                    <mat-error>Max 5 digits &amp; 3 decimals</mat-error>
                  }
                </mat-form-field>
              </td>
            </ng-container>

            <ng-container matColumnDef="margin40ft">
              <th mat-header-cell *matHeaderCellDef>40ft Margin % age <br>on All-in Freight Cost</th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <mat-form-field appearance="fill" class="small-field">
                  <input matInput formControlName="margin40ft" autocomplete="off"
                    (keypress)="restrictDecimalInput($event, 3)">
                  <span matSuffix class="percentage-symbol">%</span>
                  @if (row.get('margin40ft')?.hasError('required')) {
                    <mat-error>40ft Margin is required</mat-error>
                  }
                  @if (row.get('margin40ft')?.hasError('pattern')) {
                    <mat-error>Max 5 digits &amp; 3 decimals</mat-error>
                  }
                </mat-form-field>
              </td>
            </ng-container>

            <ng-container matColumnDef="deactivate">
              <th mat-header-cell *matHeaderCellDef>Deactivate</th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <mat-slide-toggle formControlName="isDeactivated" color="primary"></mat-slide-toggle>
              </td>
            </ng-container>

            <ng-container matColumnDef="action">
              <th mat-header-cell *matHeaderCellDef>Action</th>
              <td mat-cell *matCellDef="let row; let i = index">
                <button mat-icon-button type="button" class="delete-row-btn"
                  (click)="removeExceptionRow(i)" matTooltip="Delete row">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="exceptionColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: exceptionColumns;"></tr>
          </table>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Action Buttons -->
    <mat-card-actions>
      <button mat-button color="cancel" (click)="onCancel()">Cancel</button>
      @if ((!isEditMode && addPermission) || (isEditMode && editPermission)) {
        <button mat-button color="primary" (click)="onSave()">Save</button>
      }
    </mat-card-actions>
  </form>
</div>
